/**
 * Experience Status Derivation
 *
 * Single source of truth for experience status display.
 * Summary counts and card badges MUST both call deriveExperienceStatus().
 *
 * Returns one of: 'published' | 'draft' | 'inactive'
 *
 * Completeness rule (per SCRUM-1725 product decision, Option A — strict, 2026-04-21):
 *
 *   INACTIVE  = active !== true (overrides everything)
 *   PUBLISHED = active === true
 *               AND moderation_status === 'approved'
 *               AND has_meeting_location_name === true
 *               AND has_photos === true
 *               AND has_pricing_config === true
 *               AND has_required_sections === true
 *   DRAFT     = active === true AND NOT all-of-the-above
 *
 * Fail-closed behavior: if any required field is undefined or null
 * (i.e. the API omitted it), the experience is treated as draft.
 * Never fall back to "whatever the API happens to return" — that is
 * what produced the SCRUM-1725 Run 2 silent degradation.
 *
 * SCRUM-1725
 */

/**
 * Derive the display status for an experience.
 *
 * @param {Object} experience - Experience object from API
 * @param {boolean} experience.active - Whether the experience is active
 * @param {string} experience.moderation_status - Moderation status
 * @param {boolean} experience.has_meeting_location_name - Has meeting location
 * @param {boolean} experience.has_photos - Has at least one photo
 * @param {boolean} experience.has_pricing_config - Has non-draft pricing config
 * @param {boolean} experience.has_required_sections - All core sections published
 * @returns {'published'|'draft'|'inactive'}
 */
export function deriveExperienceStatus(experience) {
  if (!experience || experience.active !== true) {
    return 'inactive';
  }

  return isContentComplete(experience) ? 'published' : 'draft';
}

/**
 * Check whether an experience meets all Option A completeness conditions.
 * Uses strict equality (=== true, === 'approved') so that undefined/null
 * fields produce false — fail-closed by design.
 *
 * @param {Object} experience
 * @returns {boolean}
 */
function isContentComplete(experience) {
  return (
    experience.moderation_status === 'approved' &&
    experience.has_meeting_location_name === true &&
    experience.has_photos === true &&
    experience.has_pricing_config === true &&
    experience.has_required_sections === true
  );
}
